import type { ActivityEvent, Execution, WorkItemDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ScrollText } from 'lucide-react';
import { useState } from 'react';
import { CodeBlock, JsonBlock } from '@/components/code-block';
import { DefinitionList } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { ExecutionStatusBadge } from '@/components/status-badge';
import { ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { formatAbsolute, formatDuration, isEmptyObject, shortId } from '@/lib/format';
import { EXECUTION_KIND } from '@/lib/labels';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

export function WorkItemTechDetails({ item, events }: { item: WorkItemDetail; events: ActivityEvent[] | undefined }) {
  return (
    <Tabs defaultValue="executions">
      <TabsList>
        <TabsTrigger value="executions">{t('Runs')}</TabsTrigger>
        <TabsTrigger value="events">{t('Raw events')}</TabsTrigger>
        <TabsTrigger value="item">{t('Work Item (JSON)')}</TabsTrigger>
      </TabsList>
      <TabsContent value="executions">
        <ExecutionsList workItemId={item.id} />
      </TabsContent>
      <TabsContent value="events">
        {events ? <JsonBlock value={events} maxHeight="max-h-[32rem]" /> : <LoadingRows rows={2} />}
      </TabsContent>
      <TabsContent value="item">
        <JsonBlock value={item} maxHeight="max-h-[32rem]" />
      </TabsContent>
    </Tabs>
  );
}

function ExecutionsList({ workItemId }: { workItemId: string }) {
  const executions = useQuery({ queryKey: qk.workItemExecutions(workItemId), queryFn: () => api.workItems.executions(workItemId) });
  if (executions.isPending) return <LoadingRows rows={2} />;
  if (executions.isError) return <ErrorState error={executions.error} onRetry={() => void executions.refetch()} />;
  if (executions.data.length === 0) return <p className="text-sm text-muted-foreground">{t('No runs yet — no worker has been started.')}</p>;
  return (
    <div className="flex flex-col gap-2">
      {executions.data.map((ex, i) => (
        <ExecutionCard key={ex.id} ex={ex} defaultOpen={i === 0} />
      ))}
    </div>
  );
}

export function ExecutionCard({ ex, defaultOpen }: { ex: Execution; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [showLogs, setShowLogs] = useState(false);
  return (
    <div className="rounded-lg border">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left">
        <ChevronRight className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
        <ExecutionStatusBadge status={ex.status} />
        <span className="font-mono text-xs text-muted-foreground">{shortId(ex.id)}</span>
        <span className="hidden text-xs text-muted-foreground sm:inline">{EXECUTION_KIND[ex.kind] ?? ex.kind}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          <RelativeTime value={ex.createdAt} />
        </span>
      </button>
      {open && (
        <div className="border-t px-3 py-3">
          <DefinitionList
            className="text-[13px]"
            items={[
              { label: t('Execution ID'), value: <code className="font-mono text-xs">{ex.id}</code> },
              { label: t('Agent'), value: ex.agentName ?? <code className="font-mono text-xs">{ex.agentId}</code> },
              { label: t('Launcher'), value: ex.launcher || '—' },
              { label: ex.launcher === 'kubernetes' ? t('Kubernetes Job') : t('Job'), value: <code className="font-mono text-xs">{ex.kubernetesJobName ?? '—'}</code> },
              { label: ex.launcher === 'kubernetes' ? t('Pod') : t('Pod / process'), value: <code className="font-mono text-xs">{ex.kubernetesPodName ?? '—'}</code> },
              { label: t('Created'), value: formatAbsolute(ex.createdAt, true) },
              { label: t('Started'), value: formatAbsolute(ex.startedAt, true) },
              { label: t('Heartbeat'), value: ex.heartbeatAt ? <RelativeTime value={ex.heartbeatAt} /> : '—' },
              { label: t('Finished'), value: formatAbsolute(ex.finishedAt, true) },
              { label: t('Duration'), value: ex.startedAt ? formatDuration(ex.startedAt, ex.finishedAt) : '—' },
              { label: t('Exit code'), value: ex.exitCode ?? '—' },
              { label: t('Cancel requested'), value: ex.cancelRequested ? t('yes') : t('no') },
              { label: t('Provider session'), value: <code className="font-mono text-xs break-all">{ex.providerSessionReference ?? '—'}</code> },
            ]}
          />
          {ex.error && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-medium text-destructive">{t('Error')}</div>
              <CodeBlock value={ex.error} maxHeight="max-h-48" />
            </div>
          )}
          {!isEmptyObject(ex.usageMetadata) && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-medium text-muted-foreground">{t('Usage')}</div>
              <JsonBlock value={ex.usageMetadata} maxHeight="max-h-48" />
            </div>
          )}
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowLogs((s) => !s)}>
              <ScrollText /> {showLogs ? t('Hide worker logs') : t('Show worker logs')}
            </Button>
            {showLogs && <ExecutionLogs executionId={ex.id} />}
          </div>
        </div>
      )}
    </div>
  );
}

function ExecutionLogs({ executionId }: { executionId: string }) {
  const logs = useQuery({ queryKey: qk.executionLogs(executionId), queryFn: () => api.executions.logs(executionId), staleTime: 5_000 });
  if (logs.isPending) return <LoadingRows rows={2} className="mt-2" />;
  if (logs.isError) return <ErrorState className="mt-2" error={logs.error} onRetry={() => void logs.refetch()} title={t('Logs not available')} />;
  return (
    <div className="mt-2">
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>{t('Source: {source}', { source: logs.data.source || t('unknown') })}</span>
        <button type="button" className="cursor-pointer hover:text-foreground" onClick={() => void logs.refetch()}>
          {t('Reload')}
        </button>
      </div>
      <CodeBlock value={logs.data.logs || t('(no logs)')} maxHeight="max-h-[28rem]" />
    </div>
  );
}
